import { useSnackbar } from 'notistack';
import { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react';
import { TSelectItem } from '../../../../types/common';
import { checkResData } from '../../../../utils/common';
import {
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import { SITE_TASK_ORDER_TYPE } from '../../../../const/ProcessConst';
import { getSiteTaskDetail, updateSiteTask } from '../../../../apicalls/task';
import { TEditSiteTaskForm, TSiteTaskDetailData } from '../../../../types/task';
import { SITE_TASK_STATUS } from '../../../../const/TaskConst';
import {
  convertApiDateToFormDate,
  convertFormDateToApiDate,
  createSiteProcessAndSiteTaskEmployeeList,
  createSiteTaskFormMerchandiseOrderList,
  createSiteTaskFormOrderDisplayName,
  createSiteTaskFormOutSourceOrderList,
  createSiteTaskFormProcessListPulldown
} from '../../../../utils';

export type Props = {
  setIsOpen: Dispatch<boolean>;
  afterUpdate: () => void;
  afterDelete: () => void; // 削除モーダル用
  constructId: number;
  siteTaskId: number;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
  isOpen: boolean;
};

export const useHooks = (props: Props) => {
  const {
    setIsOpen,
    afterUpdate,
    afterDelete,
    constructId,
    siteTaskId,
    getValues,
    setValue,
    reset,
    isOpen
  } = props;

  // 送信可能フラグ 多重送信対策
  const isSubmitable = useRef(false);

  // 削除モーダル用
  const [isOpenSiteTaskDeleteModal, setIsOpenSiteTaskDeleteModal] =
    useState(false);
  // 削除モーダルボタン押下時のハンドラ
  const handleDeleteSubmit = () => {
    setIsOpenSiteTaskDeleteModal(true);
  };
  // 削除後のハンドラ
  const handleAfterDelete = () => {
    afterDelete();
    setIsOpen(false); // 工程削除時に編集モーダルも閉じる
  };

  const submit = (form: TEditSiteTaskForm) => {
    console.log('isSubmitable.current', isSubmitable.current);
    if (!isSubmitable.current) return;
    console.log('submit');
    isSubmitable.current = false;
    updateSiteTask(
      siteTaskId,
      form.site_process_id.value,
      form.site_task_name,
      form.status.value - 1,
      form.charge_user_id.value,
      convertFormDateToApiDate(form.planned_end_date),
      form.detail,
      Number(form.order_type),
      form.order_id.value,
      convertFormDateToApiDate(form.actual_end_date)
    ).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          setIsOpen(false);
          afterUpdate();
        } else {
          isSubmitable.current = true;
        }
      });
    });
  };

  // ステータスをAPIパラメータ用に変換
  const convStatusNumberToApiParam = (statusNum?: number) => {
    if (statusNum === undefined) return 0;
    return statusNum + 1;
  };

  // ステータス番号をステータス名に変換
  const getStatusName = (statusNum?: number) => {
    if (statusNum === undefined) return '--';
    if (SITE_TASK_STATUS.NOT_STARTED.value === statusNum)
      return SITE_TASK_STATUS.NOT_STARTED.label;
    if (SITE_TASK_STATUS.IN_PROGRESS.value === statusNum)
      return SITE_TASK_STATUS.IN_PROGRESS.label;
    if (SITE_TASK_STATUS.COMPLETED.value === statusNum)
      return SITE_TASK_STATUS.COMPLETED.label;
    if (SITE_TASK_STATUS.UNNECESSARY.value === statusNum)
      return SITE_TASK_STATUS.UNNECESSARY.label;
    return '--';
  };

  // タスクデータ
  const [siteTaskData, setSiteTaskData] = useState<TSiteTaskDetailData>();

  // 工程選択リスト
  const [siteProcessList, setSiteProcessList] = useState<TSelectItem[]>([]);

  // 担当者選択リスト
  const [employeeList, setEmployeeList] = useState<TSelectItem[]>([]);

  // ステータス選択リスト
  const [statusList, setStatusList] = useState<TSelectItem[]>([]);

  // 発注書選択リスト
  const [purchaseOrderList, setPurchaseOrderList] = useState<TSelectItem[]>([]);

  // 外注発注書リスト
  const [outsourcesOrderList, setOutsourcesOrderList] = useState<TSelectItem[]>(
    []
  );

  // 商品発注書リスト
  const [merchandiseOrderList, setMerchandiseOrderList] = useState<
    TSelectItem[]
  >([]);

  // キャンセル時の処理
  const cancelSubmit = () => {
    setIsOpen(false);
  };

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // 数値から発注書区分に変換
  const cnvNumToOrderType = (numOrderType?: number | null) => {
    if (String(numOrderType) === SITE_TASK_ORDER_TYPE.outsources)
      return SITE_TASK_ORDER_TYPE.outsources;
    if (String(numOrderType) === SITE_TASK_ORDER_TYPE.merchandise)
      return SITE_TASK_ORDER_TYPE.merchandise;
    return SITE_TASK_ORDER_TYPE.outsources;
  };

  // ステータスリストの作成
  const createStatusList = () => {
    const list: TSelectItem[] = [];
    list.push({ value: 0, label: '--' });
    list.push({
      value: SITE_TASK_STATUS.NOT_STARTED.value + 1,
      label: SITE_TASK_STATUS.NOT_STARTED.label
    });
    list.push({
      value: SITE_TASK_STATUS.IN_PROGRESS.value + 1,
      label: SITE_TASK_STATUS.IN_PROGRESS.label
    });
    list.push({
      value: SITE_TASK_STATUS.COMPLETED.value + 1,
      label: SITE_TASK_STATUS.COMPLETED.label
    });
    list.push({
      value: SITE_TASK_STATUS.UNNECESSARY.value + 1,
      label: SITE_TASK_STATUS.UNNECESSARY.label
    });
    setStatusList(list);
  };

  // 工程リストから工程IDに該当する工程名を取得
  const getProcessNameFromProcessList = (
    processId: number | undefined,
    targetProcessList: TSelectItem[]
  ) => {
    return targetProcessList
      .filter(process => {
        return process.value === processId;
      })
      .map(process => {
        return process.label;
      })[0];
  };

  // 発注書区分変更時のハンドラ
  const handleChangeOrderTypeRadio = (e: ChangeEvent<HTMLInputElement>) => {
    const currentOrderType = e.target.value;
    setValue('order_type', currentOrderType, { shouldDirty: true });

    // セレクトボックスの内容を切り替え
    updatePurchaseOrderList(currentOrderType);

    // 発注書区分が初期値に戻された場合、発注書セレクトボックスの初期値をタスク詳細の値に戻す
    const orderCategory = cnvNumToOrderType(siteTaskData?.order_category);
    if (orderCategory === getValues('order_type')) {
      setValue('order_id', {
        value: siteTaskData?.order?.id,
        label: createSiteTaskFormOrderDisplayName(siteTaskData?.order)
      });
    } else {
      setValue('order_id', { value: 0, label: '--' });
    }
  };

  // 発注書区分に応じたセレクトボックスの内容を設定
  const updatePurchaseOrderList = (currentOrderType: string) => {
    const orderList =
      currentOrderType === SITE_TASK_ORDER_TYPE.outsources
        ? outsourcesOrderList
        : merchandiseOrderList;
    setPurchaseOrderList(orderList);
  };

  // セレクトボックスの内容を反映
  useEffect(() => {
    updatePurchaseOrderList(cnvNumToOrderType(siteTaskData?.order_category));
  }, [outsourcesOrderList, merchandiseOrderList]);

  useEffect(() => {
    // データ取得を非同期で行う
    async function fetchData() {
      if (isOpen) {
        if (siteTaskId > 0) {
          const resData = await getSiteTaskDetail(siteTaskId);
          const data = await checkResData(resData, enqueueSnackbar);
          if (data) {
            const tmpSiteTaskData = data.data;
            setSiteTaskData(tmpSiteTaskData);
            const tmpSiteProcessList =
              await createSiteTaskFormProcessListPulldown(
                constructId,
                enqueueSnackbar
              );
            setSiteProcessList(tmpSiteProcessList);
            const employeeList = await createSiteProcessAndSiteTaskEmployeeList(
              constructId,
              enqueueSnackbar
            );
            setEmployeeList(employeeList);
            createStatusList();

            setValue('site_task_name', tmpSiteTaskData.site_task_name);
            setValue(
              'planned_end_date',
              convertApiDateToFormDate(tmpSiteTaskData.planned_end_date)
            );
            setValue('detail', tmpSiteTaskData.detail);
            setValue('status', {
              value: convStatusNumberToApiParam(tmpSiteTaskData.status),
              label: getStatusName(tmpSiteTaskData.status)
            });
            setValue(
              'actual_end_date',
              convertApiDateToFormDate(tmpSiteTaskData.actual_end_date)
            );
            setValue(
              'order_type',
              cnvNumToOrderType(tmpSiteTaskData.order_category)
            );
            setValue('order_id', {
              value: tmpSiteTaskData.order?.id ?? 0,
              label: createSiteTaskFormOrderDisplayName(tmpSiteTaskData.order)
            });
            setValue('site_process_id', {
              value: tmpSiteTaskData.site_process.id ?? 0,
              label:
                getProcessNameFromProcessList(
                  tmpSiteTaskData.site_process.id ?? 0,
                  tmpSiteProcessList
                ) ?? '--'
            });
            setValue('charge_user_id', {
              value: tmpSiteTaskData.charge_user?.id ?? 0,
              label: tmpSiteTaskData.charge_user?.name ?? '--'
            });

            const outsourcesOrderList =
              await createSiteTaskFormOutSourceOrderList(enqueueSnackbar);
            setOutsourcesOrderList(outsourcesOrderList);
            const merchandiseOrderList =
              await createSiteTaskFormMerchandiseOrderList(enqueueSnackbar);

            createStatusList();
          }
          // オープン時に送信可能にする
          isSubmitable.current = true;
        }
      } else {
        // フォームの初期化
        setSiteTaskData(undefined);
        setSiteProcessList([]);
        setEmployeeList([]);
        setStatusList([]);
        // 各フィールドを初期化
        setValue('site_process_id', { value: null, label: '' });
        setValue('site_task_name', null);
        setValue('charge_user_id', { value: null, label: '' });
        setValue('planned_end_date', null);
        setValue('detail', null);
        setValue('status', { value: null, label: '' });
        setValue('actual_end_date', null);
        setValue('order_type', null);
        setValue('order_id', { value: null, label: '' });
      }
    }
    // データ取得を行う
    fetchData();
  }, [isOpen]);

  return {
    submit,
    cancelSubmit,
    handleChangeOrderTypeRadio,
    siteProcessList,
    purchaseOrderList,
    employeeList,
    statusList,
    handleDeleteSubmit,
    isOpenSiteTaskDeleteModal,
    setIsOpenSiteTaskDeleteModal,
    handleAfterDelete,
    siteTaskData
  };
};
